import type { CellType } from 'handsontable/cellTypes';
import { _isNil, _notNil } from '@/littledash';
import type { ReactNode } from 'react';
import type { CustomColumnSettings, CustomValidation, SpreadSheetError } from './SpreadSheet.model';

export type SpreadSheetErrorMessage = (value: ReactNode) => string;

export const spreadSheetValidationTooltip = 'There are invalid cells';

export const formatValidationErrors = (errors: Record<string, SpreadSheetError>): Array<string> =>
  Object.keys(errors).map((column: string) => createValidationError(errors[column], ''));

export const createValidationError = (
  error: SpreadSheetError,
  type?: string,
  customMessage?: SpreadSheetErrorMessage
): string => {
  if ((_isNil(error.value) || error.value === '') && _isNil(customMessage)) {
    return 'Required Field';
  } else {
    return customMessage?.(error.value) ?? errorCellTypes[type as CellType]?.(error.value) ?? `Is not valid`;
  }
};

export const buildCustomValidators = (columns: Array<CustomColumnSettings>): Record<string, CustomValidation> =>
  columns.reduce<Record<string, CustomValidation>>((acc, { data, validationErrorMessage, linkedValidationColumns }) => {
    if (_notNil(data)) {
      return { ...acc, [data.toString()]: { validationErrorMessage, linkedValidationColumns } };
    } else {
      return acc;
    }
  }, {});

type CustomCellTypes = 'numeric-expo' | 'multi-select' | 'date-wrapper';

const errorCellTypes: Record<CellType | CustomCellTypes, SpreadSheetErrorMessage> = {
  dropdown: () => `Is not a valid option`,
  date: () => `Must be a valid date`,
  numeric: (value) => `${value ?? 'Value'} must be a number`,
  time: () => `Must be a valid time`,
  autocomplete: () => `Is not valid`,
  handsontable: () => `Is not valid`,
  password: () => `Is not valid`,
  text: () => `Must have a max length of 250 characters`,
  checkbox: () => `Is not valid`,
  'numeric-expo': () => `Value must be a number`,
  'multi-select': () => `Is not a valid option`,
  'date-wrapper': () => `Must be a valid YYYY-MM-DD date`,
};
